import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import experiences from "./experiences";
import { getSkillColor } from "../../utils/getUniqueSkills";
import { getExperienceFilename } from "../terminal/fileSystem";

const ExperienceSection = ({ hoveredSkill, forceCollapsed = false, onTerminalCommand }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [expandedTech, setExpandedTech] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setCollapsed(forceCollapsed);
  }, [forceCollapsed]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const toggleTech = (index) => {
    setExpandedTech((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const cardMatchesSkill = (exp) => {
    if (!hoveredSkill) return true;
    return exp.technologies.split(", ").map((t) => t.trim()).includes(hoveredSkill);
  };

  return (
    <section
      className="py-4 border-b"
      style={{ borderColor: "var(--border-color)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3 }}
      >
        <button
          onClick={() => !forceCollapsed && setCollapsed(!collapsed)}
          className="flex items-center gap-2 w-full text-left mb-3"
        >
          <h2
            className="text-xl font-bold"
            style={{ color: "var(--text-primary)" }}
          >
            Experience
          </h2>
          <span
            className="text-xs transition-transform duration-200"
            style={{
              color: "var(--text-tertiary)",
              transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            }}
          >
            ▼
          </span>
        </button>

        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
        <div className="space-y-2">
          {experiences.map((exp, index) => (
            <div
              key={index}
              className="p-3 rounded-lg transition-all duration-300 relative overflow-hidden cursor-pointer"
              style={{
                backgroundColor:
                  hoveredCard === index
                    ? "var(--bg-card-hover)"
                    : "var(--bg-card)",
                border: hoveredSkill && cardMatchesSkill(exp)
                  ? "1px solid rgba(200, 210, 230, 0.3)"
                  : "1px solid rgba(255, 255, 255, 0.05)",
                opacity: cardMatchesSkill(exp) ? 1 : 0.25,
                transition: "opacity 0.3s ease, border-color 0.3s ease",
              }}
              onClick={() => onTerminalCommand?.(`cat experience/${getExperienceFilename(exp.company, exp.start)}`)}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              onMouseMove={handleMouseMove}
            >
              {/* Gloss effect */}
              {hoveredCard === index && (
                <div
                  className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                  style={{
                    background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.05) 0%, transparent 50%)`,
                    opacity: 0.5,
                  }}
                />
              )}

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3
                      className="text-base font-medium"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {exp.company}
                    </h3>
                    <p
                      className="text-sm"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {exp.positionName}
                    </p>
                    <p
                      className="text-xs mt-1"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {exp.start} - {exp.end}
                    </p>
                  </div>
                  {exp.logo && (
                    <img
                      src={exp.logo}
                      alt={exp.company}
                      className="h-8 object-contain ml-3 rounded-sm"
                    />
                  )}
                </div>

                {/* Expandable Tech Stack */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleTech(index); }}
                  className="text-xs hover:opacity-70 transition-opacity"
                  style={{ color: "var(--accent)" }}
                >
                  {expandedTech[index]
                    ? "Hide tech stack ↑"
                    : "Show tech stack ↓"}
                </button>

                <AnimatePresence>
                  {(expandedTech[index] || (hoveredSkill && cardMatchesSkill(exp))) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {exp.technologies.split(", ").map((tech, i) => (
                          <span
                            key={i}
                            className="px-1.5 py-px rounded text-xs"
                            style={{
                              backgroundColor: tech === hoveredSkill
                                ? "rgba(255, 255, 255, 0.08)"
                                : "rgba(255, 255, 255, 0.03)",
                              color: getSkillColor(tech),
                              opacity: hoveredSkill && tech !== hoveredSkill ? 0.5 : 1,
                            }}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default ExperienceSection;
